/**
 * Placed Tile Rendering System
 * 
 * Renders tiles placed by the tile editor on top of procedural content.
 * Handles loading, rendering, and unloading of placed tiles per chunk.
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '../types';
import { SFX_KEYS, playSound } from './SoundManager';

export interface PlacedTile {
  worldX: number;
  worldY: number;
  objectType: string;
  layoutId: string;
  objectId?: string;
}

interface PlacedTileSprite {
  graphics: Phaser.GameObjects.Graphics;
  worldX: number;
  worldY: number;
  objectType: string;
}

export class PlacedTileRenderer {
  private scene: Phaser.Scene;
  private placedTiles: Map<string, PlacedTileSprite[]> = new Map(); // chunkId -> tiles
  private loadedChunks: Set<string> = new Set();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Load placed tiles for a chunk
   */
  loadChunk(chunkId: string, tiles: PlacedTile[]): void {
    // Unload existing tiles for this chunk (in case it's being reloaded with new data)
    if (this.loadedChunks.has(chunkId)) {
      this.unloadChunk(chunkId);
    }

    const chunkTiles: PlacedTileSprite[] = [];

    for (const tile of tiles) {
      const sprite = this.renderPlacedTile(tile);
      if (sprite) {
        chunkTiles.push(sprite);
      }
    }

    this.placedTiles.set(chunkId, chunkTiles);
    this.loadedChunks.add(chunkId);

    if (tiles.length > 0) {
      console.log(`🗺️ Rendered ${tiles.length} placed tiles for chunk ${chunkId}`);
    }
  }

  /**
   * Unload placed tiles for a chunk
   */
  unloadChunk(chunkId: string): void {
    const tiles = this.placedTiles.get(chunkId);
    if (tiles) {
      for (const tile of tiles) {
        tile.graphics.destroy();
      }
      this.placedTiles.delete(chunkId);
    }
    this.loadedChunks.delete(chunkId);
  }

  /**
   * Clear all placed tiles
   */
  clearAll(): void {
    this.placedTiles.forEach((tiles) => {
      for (const tile of tiles) {
        tile.graphics.destroy();
      }
    });
    this.placedTiles.clear();
    this.loadedChunks.clear();
  }

  /**
   * Render a single placed tile
   */
  private renderPlacedTile(tile: PlacedTile): PlacedTileSprite | null {
    const graphics = this.scene.add.graphics();
    graphics.setDepth(5); // Above water, below ships

    const { worldX, worldY, objectType } = tile;

    // Render based on object type
    switch (objectType) {
      case 'island':
        this.renderIslandTile(graphics, worldX, worldY);
        break;
      case 'structure':
        this.renderStructureTile(graphics, worldX, worldY);
        break;
      case 'hazard':
        this.renderHazardTile(graphics, worldX, worldY);
        break;
      case 'decoration':
        this.renderDecorationTile(graphics, worldX, worldY);
        break;
      // Handle construction tiles (construction_wood, construction_stone, construction_metal)
      case 'construction_wood':
        this.renderConstructionTile(graphics, worldX, worldY, 0xd2691e); // Chocolate brown
        break;
      case 'construction_stone':
        this.renderConstructionTile(graphics, worldX, worldY, 0x808080); // Gray
        break;
      case 'construction_metal':
        this.renderConstructionTile(graphics, worldX, worldY, 0xa9a9a9); // Dark gray
        break;
      default:
        console.warn(`Unknown placed tile type: ${objectType}`);
        graphics.destroy();
        return null;
    }

    return {
      graphics,
      worldX,
      worldY,
      objectType,
    };
  }

  /**
   * Render an island tile (solid land)
   */
  private renderIslandTile(graphics: Phaser.GameObjects.Graphics, worldX: number, worldY: number): void {
    // Draw as a solid tile with island color
    graphics.fillStyle(0x228b22, 1.0); // Forest green
    graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
    
    // Add border for visibility
    graphics.lineStyle(1, 0x1a5f1a, 0.5);
    graphics.strokeRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
  }

  /**
   * Render a structure tile (buildings, docks, etc.)
   */
  private renderStructureTile(graphics: Phaser.GameObjects.Graphics, worldX: number, worldY: number): void {
    // Draw as a brown/gray structure
    graphics.fillStyle(0x8b4513, 1.0); // Brown
    graphics.fillRect(worldX + 2, worldY + 2, TILE_SIZE - 4, TILE_SIZE - 4);
    
    // Add border
    graphics.lineStyle(2, 0x654321, 1.0);
    graphics.strokeRect(worldX + 2, worldY + 2, TILE_SIZE - 4, TILE_SIZE - 4);
  }

  /**
   * Render a hazard tile (rocks, reefs, etc.)
   */
  private renderHazardTile(graphics: Phaser.GameObjects.Graphics, worldX: number, worldY: number): void {
    // Draw as a dark gray hazard
    graphics.fillStyle(0x696969, 1.0); // Dark gray
    graphics.fillRect(worldX + 1, worldY + 1, TILE_SIZE - 2, TILE_SIZE - 2);
    
    // Add warning border
    graphics.lineStyle(2, 0xff4500, 0.8); // Orange-red warning
    graphics.strokeRect(worldX + 1, worldY + 1, TILE_SIZE - 2, TILE_SIZE - 2);
  }

  /**
   * Render a decoration tile (non-collision objects)
   */
  private renderDecorationTile(graphics: Phaser.GameObjects.Graphics, worldX: number, worldY: number): void {
    // Draw as a light decorative element
    graphics.fillStyle(0x87ceeb, 0.5); // Light blue, semi-transparent
    graphics.fillCircle(worldX + TILE_SIZE / 2, worldY + TILE_SIZE / 2, TILE_SIZE / 3);
  }

    /**
     * Render a construction tile (player-placed) - Terraria pixel art style
     */
    private renderConstructionTile(graphics: Phaser.GameObjects.Graphics, worldX: number, worldY: number, color: number): void {
       // Terraria-style construction tiles with pixel-perfect appearance
       const tileSize = TILE_SIZE; // Construction tiles are TILE_SIZE x TILE_SIZE pixels (16x16)
      
      // Determine material-specific colors and pattern
      let darkColor: number;
      let lightColor: number;
      let accentColor: number;
      let metallic: boolean = false;
      
      switch (color) {
        case 0xd2691e: // Wood
          darkColor = 0x8b4513;
          lightColor = 0xdaa520;
          accentColor = 0xa0522d;
          break;
        case 0x808080: // Stone
          darkColor = 0x696969;
          lightColor = 0xa9a9a9;
          accentColor = 0x708090;
          break;
        case 0xa9a9a9: // Metal
          darkColor = 0x696969;
          lightColor = 0xc0c0c0;
          accentColor = 0x808080;
          metallic = true;
          break;
        default:
          darkColor = 0x4a4a4a;
          lightColor = 0xcccccc;
          accentColor = 0x808080;
      }
      
      // Main tile body with subtle shading
      graphics.fillStyle(color, 0.85);
      graphics.fillRect(worldX, worldY, tileSize, tileSize);
       
       // Add subtle darker shading on bottom and right edges
       graphics.fillStyle(darkColor, 0.2);
       graphics.fillRect(worldX, worldY + tileSize - 1, tileSize, 1);        // Bottom edge
       graphics.fillRect(worldX + tileSize - 1, worldY, 1, tileSize);        // Right edge
       
       // Add subtle lighter shading on top and left edges
       graphics.fillStyle(lightColor, 0.15);
       graphics.fillRect(worldX, worldY, tileSize, 1);                       // Top edge
       graphics.fillRect(worldX, worldY, 1, tileSize);                       // Left edge
       
       // Add detailed texture based on material
       if (metallic) {
         // Metal texture - horizontal line pattern
         graphics.fillStyle(0x707070, 0.25);
         graphics.fillRect(worldX + 1, worldY + 4, tileSize - 2, 1);
         graphics.fillRect(worldX + 1, worldY + 9, tileSize - 2, 1);
         
         // Metal shine spots
         graphics.fillStyle(0xe0e0e0, 0.3);
         graphics.fillRect(worldX + 2, worldY + 2, 2, 2);
         graphics.fillRect(worldX + 11, worldY + 10, 2, 2);
       } else {
         // Wood/Stone texture - random detail dots with varied opacity
         graphics.fillStyle(accentColor, 0.25);
         const seed = (worldX + worldY) * 73; // Deterministic pattern
         const rand = (x: number) => Math.abs(Math.sin(x) * 10000) % 1;
         
         for (let i = 0; i < 6; i++) {
           for (let j = 0; j < 6; j++) {
             const px = worldX + 2 + i * 2;
             const py = worldY + 2 + j * 2;
             const randomValue = rand(seed + i * 7 + j * 11);
             
             // Only draw some dots (creates sparse pattern)
             if (randomValue > 0.4) {
               const size = randomValue > 0.7 ? 1 : 0.5;
               const opacity = randomValue * 0.3;
               graphics.fillStyle(darkColor, opacity);
               graphics.fillRect(px, py, size, size);
             }
           }
         }
       }
       
       // Very subtle border - barely visible gray outline instead of bright yellow
       graphics.lineStyle(0.5, 0x888888, 0.3);
       graphics.strokeRect(worldX + 0.5, worldY + 0.5, tileSize - 1, tileSize - 1);
    }

  /**
   * Get loaded chunks
   */
  getLoadedChunks(): Set<string> {
    return this.loadedChunks;
  }

   /**
    * Get all placed tiles
    */
   getPlacedTiles(): Map<string, PlacedTileSprite[]> {
     return this.placedTiles;
   }

   /**
    * Check if a tile is blocked by a placed tile
    * Collision blocking types: island, structure, hazard, construction_*
    * Non-blocking types: decoration
    */
   isTileBlockedByPlacedTile(worldX: number, worldY: number): boolean {
     // Check all loaded chunks
     for (const tiles of this.placedTiles.values()) {
       for (const tile of tiles) {
         // Check if world position overlaps with tile
         if (
           worldX === tile.worldX &&
           worldY === tile.worldY
         ) {
           // Decoration tiles don't block movement
           if (tile.objectType === 'decoration') {
             return false;
           }
           // All other types block: island, structure, hazard, construction_*
           return true;
         }
       }
     }
     return false;
   }

  /**
   * Render a single construction tile incrementally (optimistic or real-time update)
   * Used for optimistic rendering and incremental updates without full chunk reload
   */
  renderSingleConstructionTile(worldX: number, worldY: number, materialType: string, constructionId: string): void {
    // Get chunk ID for this tile
    const CHUNK_SIZE = 1000;
    const chunkX = Math.floor(worldX / CHUNK_SIZE);
    const chunkY = Math.floor(worldY / CHUNK_SIZE);
    const chunkId = `${chunkX}_${chunkY}`;

    // Get color based on material type
    const colorMap: Record<string, number> = {
      wood: 0xd2691e,    // Chocolate brown
      stone: 0x808080,   // Gray
      metal: 0xa9a9a9,   // Dark gray
    };
    const color = colorMap[materialType] || 0x808080;

    // Create graphics object
    const graphics = this.scene.add.graphics();
    graphics.setDepth(5); // Above water, below ships
    graphics.setData('constructionId', constructionId);
    
    // Start invisible for fade-in animation
    graphics.setAlpha(0);

    // Render the construction tile
    this.renderConstructionTile(graphics, worldX, worldY, color);
    
    // Fade-in animation (300ms)
    this.scene.tweens.add({
      targets: graphics,
      alpha: 1,
      duration: 300,
      ease: 'Cubic.easeOut',
    });
    
    // Particle effect - dust/sparkle on placement
    this.createPlacementParticles(worldX, worldY, color);
    
    // Play construction sound
    playSound(SFX_KEYS.CONSTRUCTION_PLACE, 0.3);

    // Add to the chunk's tile list
    if (!this.placedTiles.has(chunkId)) {
      this.placedTiles.set(chunkId, []);
    }
    this.placedTiles.get(chunkId)!.push({
      graphics,
      worldX,
      worldY,
      objectType: `construction_${materialType}`,
    });

    console.log(`🔨 [PlacedTileRenderer] Rendered single ${materialType} tile at (${worldX}, ${worldY}) with ID ${constructionId}`);
  }

  /**
   * Remove a single construction tile incrementally
   * Used for incremental updates without full chunk reload
   */
  removeSingleConstructionTile(worldX: number, worldY: number, constructionId?: string): void {
    // Get chunk ID for this tile
    const CHUNK_SIZE = 1000;
    const chunkX = Math.floor(worldX / CHUNK_SIZE);
    const chunkY = Math.floor(worldY / CHUNK_SIZE);
    const chunkId = `${chunkX}_${chunkY}`;

    const chunkTiles = this.placedTiles.get(chunkId);
    if (!chunkTiles) {
      console.warn(`🔨 [PlacedTileRenderer] No tiles found for chunk ${chunkId}`);
      return;
    }

    // Find and remove the tile at this position (or with this ID)
    const tileIndex = chunkTiles.findIndex(tile => {
      if (constructionId) {
        return tile.graphics.getData('constructionId') === constructionId;
      }
      return tile.worldX === worldX && tile.worldY === worldY;
    });

    if (tileIndex >= 0) {
      const tile = chunkTiles[tileIndex];
      
      // Fade-out animation (200ms) before destroying
      this.scene.tweens.add({
        targets: tile.graphics,
        alpha: 0,
        duration: 200,
        ease: 'Cubic.easeIn',
        onComplete: () => {
          tile.graphics.destroy();
        },
      });
      
      // Particle effect - debris/dust on removal
      this.createRemovalParticles(worldX, worldY);
      
      // Play destruction sound
      playSound(SFX_KEYS.CONSTRUCTION_REMOVE, 0.3);
      
      chunkTiles.splice(tileIndex, 1);
      console.log(`🔨 [PlacedTileRenderer] Removed tile at (${worldX}, ${worldY}) from chunk ${chunkId}`);
    } else {
      console.warn(`🔨 [PlacedTileRenderer] Tile not found at (${worldX}, ${worldY}) in chunk ${chunkId}`);
    }
  }

  /**
   * Check if a tile already exists at the given world coordinates
   */
  hasTileAt(worldX: number, worldY: number): boolean {
    const CHUNK_SIZE = 1000;
    const chunkX = Math.floor(worldX / CHUNK_SIZE);
    const chunkY = Math.floor(worldY / CHUNK_SIZE);
    const chunkId = `${chunkX}_${chunkY}`;

    const chunkTiles = this.placedTiles.get(chunkId);
    if (!chunkTiles) return false;

    return chunkTiles.some(t => t.worldX === worldX && t.worldY === worldY);
  }

  /**
   * Update construction tile ID (used when converting optimistic temp tile to real tile)
   */
  updateConstructionTileId(worldX: number, worldY: number, oldId: string, newId: string): void {
    const CHUNK_SIZE = 1000;
    const chunkX = Math.floor(worldX / CHUNK_SIZE);
    const chunkY = Math.floor(worldY / CHUNK_SIZE);
    const chunkId = `${chunkX}_${chunkY}`;

    const chunkTiles = this.placedTiles.get(chunkId);
    if (!chunkTiles) return;

    const tile = chunkTiles.find(t => t.graphics.getData('constructionId') === oldId);
    if (tile) {
      tile.graphics.setData('constructionId', newId);
      console.log(`🔨 [PlacedTileRenderer] Updated tile ID from ${oldId} to ${newId}`);
    }
  }

   /**
    * Create particle effect for tile placement
    */
   private createPlacementParticles(worldX: number, worldY: number, color: number): void {
     // Create a simple particle effect using graphics
     const particleCount = 8;
     const centerX = worldX + TILE_SIZE / 2;
     const centerY = worldY + TILE_SIZE / 2;

     for (let i = 0; i < particleCount; i++) {
       const angle = (Math.PI * 2 * i) / particleCount;
       const distance = 20 + Math.random() * 15;
       const targetX = centerX + Math.cos(angle) * distance;
       const targetY = centerY + Math.sin(angle) * distance;

       const particle = this.scene.add.graphics();
       particle.setPosition(centerX, centerY); // Set initial position
       particle.setDepth(6); // Above tiles
       particle.fillStyle(color, 1.0);
       particle.fillCircle(0, 0, 2); // Draw at origin (0,0) since position is set above

       this.scene.tweens.add({
         targets: particle,
         x: targetX,
         y: targetY,
         alpha: 0,
         duration: 400,
         ease: 'Cubic.easeOut',
         onComplete: () => {
           particle.destroy();
         },
       });
     }
   }

   /**
    * Create particle effect for tile removal
    */
   private createRemovalParticles(worldX: number, worldY: number): void {
     // Create debris particles
     const particleCount = 6;
     const centerX = worldX + TILE_SIZE / 2;
     const centerY = worldY + TILE_SIZE / 2;

     for (let i = 0; i < particleCount; i++) {
       const angle = Math.random() * Math.PI * 2;
       const distance = 15 + Math.random() * 20;
       const targetX = centerX + Math.cos(angle) * distance;
       const targetY = centerY + Math.sin(angle) * distance;

       const particle = this.scene.add.graphics();
       particle.setPosition(centerX, centerY); // Set initial position
       particle.setDepth(6);
       particle.fillStyle(0x8b4513, 0.8); // Brown debris
       particle.fillRect(-2, -2, 4, 4); // Draw at origin since position is set above

       this.scene.tweens.add({
         targets: particle,
         x: targetX,
         y: targetY + 20, // Gravity effect
         alpha: 0,
         duration: 500,
         ease: 'Quad.easeIn',
         onComplete: () => {
           particle.destroy();
         },
       });
     }
   }
}
